/**
 * What opens a chunk, sentence or pattern the climb holds back (v1 ticket 29),
 * in the learner's words: "Opens when you know cafè". The server sends the
 * lock (`opens`); never a box. Target words sit in `<bdi dir="auto">`
 * isolates, so they keep their own direction inside a right-to-left line.
 */
import { html, type TemplateResult } from 'lit';
import { t } from './i18n';
import type { Opens, PatternMark } from './types';

const word = (w: string) => html`<bdi class="ca" dir="auto">${w}</bdi>`;

/** Target words joined in the interface's own way: "a", "a or b", "a and b", "a, b and c". */
function joined(words: readonly string[], last: 'or' | 'and'): TemplateResult {
  const comma = t('climb.comma');
  const tail = t(last === 'or' ? 'climb.or' : 'climb.and');
  return html`${words.map((w, i) => html`${i === 0 ? '' : i === words.length - 1 ? tail : comma}${word(w)}`)}`;
}

/** A translated line with its `{words}` filled by a template. */
function fill(key: string, words: TemplateResult, vars?: Record<string, number>): TemplateResult {
  const [pre, post = ''] = t(key, vars).split('{words}');
  return html`${pre}${words}${post}`;
}

/** The one line that says what opens an item. */
export function opensLine(o: Opens): TemplateResult {
  if ('lines' in o) return html`${t('climb.opensLines', { need: o.need, n: o.lines })}`;
  // A Persian word in Finglish where the server knows its spelling, as the learner reads it first.
  const words = o.words.map((w, i) => o.latin?.[i] || w);
  if (o.need >= words.length) return fill('climb.opens', joined(words, 'and'));
  if (o.need === 1 && words.length === 2) return fill('climb.opens', joined(words, 'or'));
  return fill('climb.opensSome', joined(words, 'and'), { n: o.need });
}

/** A pattern held in a line: "Pattern: vull + something, in 4 of your lines". */
export function patternNote(p: PatternMark): TemplateResult {
  const frame = html`${word(p.anchors.join(' '))} + <bdi dir="auto">${p.slot ?? '…'}</bdi>`;
  return fill('climb.pattern', frame, { n: p.lines });
}
